import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Get the Google Maps Geocoder instance
 * Lazily initializes the geocoder when first called
 */
const getGeocoder = () => {
  if (!window.google?.maps?.Geocoder) {
    throw new Error("Google Maps JavaScript API not loaded");
  }
  return new window.google.maps.Geocoder();
};

/**
 * Promisified wrapper for Google Maps Geocoder
 * @param {Object} request - Geocoder request object
 * @returns {Promise<Object>} - Geocoder results
 */
const geocode = (request) => {
  return new Promise((resolve, reject) => {
    const geocoder = getGeocoder();
    geocoder.geocode(request, (results, status) => {
      if (status === "OK") {
        resolve({ status, results });
      } else if (status === "ZERO_RESULTS") {
        resolve({ status, results: [] });
      } else {
        reject(new Error(`Geocoder failed: ${status}`));
      }
    });
  });
};

/**
 * Custom hook for forward geocoding (address to coordinates)
 * Uses Google Maps JavaScript API Geocoder (no CORS issues)
 * 
 * @returns {Object} - { mutateAsync, isLoading, error, data }
 * 
 * @example
 * const geocodeMutation = useGeocodeByAddress();
 * const result = await geocodeMutation.mutateAsync(address);
 */
export const useGeocodeByAddress = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const mutateAsync = useCallback(async (address) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await geocode({ address });
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      console.error("Geocode by address failed:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutateAsync, isLoading, error, data };
};

/**
 * Custom hook for reverse geocoding (coordinates to address)
 * Uses Google Maps JavaScript API Geocoder (no CORS issues)
 * Includes simple caching to prevent redundant API calls
 * 
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude  
 * @param {boolean} enabled - Whether the query should run
 * @returns {Object} - { data, isLoading, error }
 * 
 * @example
 * const { data, isLoading } = useReverseGeocode(42.924, -85.596, true);
 */
export const useReverseGeocode = (lat, lng, enabled = false) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Simple cache to prevent redundant calls
  const cacheRef = useRef(new Map());
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!enabled || lat == null || lng == null) {
      return;
    }

    // Check if Google Maps is loaded
    if (!window.google?.maps?.Geocoder) {
      return;
    }

    const cacheKey = `${lat},${lng}`;
    
    // Skip if same coordinates
    if (lastKeyRef.current === cacheKey) {
      return;
    }
    
    lastKeyRef.current = cacheKey;

    // Check cache first
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setData(cached.data);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await geocode({
          location: { lat, lng },
        });
        
        // Cache the result
        cacheRef.current.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        });
        
        setData(result);
      } catch (err) {
        setError(err);
        console.error("Reverse geocode failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [lat, lng, enabled]);

  return { data, isLoading, error };
};
